import {
  ApiOptions,
  CloseoutTrackerStatusType,
  Maybe,
  Patient,
  SiteId,
  StateAssert,
  Study,
  VisitLocationType,
  VisitVariantType,
  ZonedDateTime,
} from '@csp/csp-common-model';
import {
  ScheduleReferenceDates,
  RequestWindow,
  ScheduleRequestRef,
  SchedulingService,
} from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import { ZoomService } from '@csp/csp-common-zoom';
import { toRestOptions } from '@csp/csp-fe-auth';
import { AppointmentIncludeFieldV1 } from '@csp/dmdp-api-appointment-dto';
import { AppointmentRestServiceV1 } from '@csp/dmdp-api-client';
import { largePage, Page } from '@csp/dmdp-api-common-dto';
import { BioSampleQueryService } from '@csp/csp-common-hbs';
import { BioSample } from '@csp/csp-common-hbs-model';
import { PatientVisitMetaService } from '@csp/csp-common-patient-mapper';
import { WindowInfo } from '@csp/csp-common-date-util';
import { uniq } from 'lodash';
import { VisitMapper } from '../mapper/VisitMapper';
import { VisitQueryMapper } from '../mapper/VisitQueryMapper';
import { BookedVisit } from '../model/BookedVisit';
import { BookedVisitsPage } from '../model/BookedVisitsPage';
import { CancelSettings } from '../model/CancelSettings';
import { CloseoutInfo } from '../model/CloseoutInfo';
import { BookedVisitQueryIn } from '../model/query/BookedVisitQueryIn';
import { UnbookedVisit } from '../model/UnbookedVisit';
import { VisitIn } from '../model/VisitIn';
import { VisitRequest } from '../schedule/model/VisitRequest';
import { VisitScheduleService } from '../schedule/service/VisitScheduleService';

const deleteVideoMeetingIfNeeded = async (existingVisit: BookedVisit, apiOptions?: ApiOptions): Promise<void> => {
  const videoMeetingIdAsNumber = Number(existingVisit.location.videoMeetingId);
  if (!isNaN(videoMeetingIdAsNumber)) {
    try {
      await ZoomService.deleteMeeting(videoMeetingIdAsNumber, apiOptions);
    } catch (e) {
      // Don't fail on missing meeting
    }
  }
};

const createVideoMeetingIfNeeded = async (
  patient: Patient,
  { locationType, date, time }: VisitIn,
  apiOptions?: ApiOptions,
): Promise<Maybe<string>> => {
  if (locationType === VisitLocationType.VIDEO) {
    const zoomMeeting = await ZoomService.createMeetingForUserAndCurrentTenant(
      { user: patient, meetingOptions: { date, time } },
      apiOptions,
    );
    return `${zoomMeeting.meetingId}`;
  } else {
    return undefined;
  }
};

const addPatientVisit = async (
  patient: Patient,
  visitIn: VisitIn,
  unbookedVisit: UnbookedVisit,
  apiOptions?: ApiOptions,
): Promise<void> => {
  const videoMeetingId = await createVideoMeetingIfNeeded(patient, visitIn, apiOptions);
  const appointmentInV1 = VisitMapper.toAppointmentInV1(
    patient.userId,
    visitIn,
    videoMeetingId,
    unbookedVisit.requestRef,
    patient.siteId,
  );
  const appointmentV1 = await AppointmentRestServiceV1.create(toRestOptions(apiOptions), appointmentInV1);
  await PatientVisitMetaService.upsertVisit(patient, appointmentV1, unbookedVisit?.request.variant);
};

const updateVisit = async (
  existingVisit: BookedVisit,
  patient: Patient,
  visitIn: VisitIn,
  visitRequest: VisitRequest,
  apiOptions?: ApiOptions,
): Promise<void> => {
  await deleteVideoMeetingIfNeeded(existingVisit);
  const videoMeetingId = await createVideoMeetingIfNeeded(patient, visitIn, apiOptions);
  const { location, startTimestamp } = VisitMapper.toAppointmentInV1(
    patient.userId,
    visitIn,
    videoMeetingId,
    visitRequest,
    patient.siteId,
  );

  const restOptions = toRestOptions(apiOptions);
  await AppointmentRestServiceV1.updateStartTimestamp(restOptions, existingVisit.visitId, { startTimestamp });
  if (location) {
    await AppointmentRestServiceV1.updateLocation(restOptions, existingVisit.visitId, location);
  }
  if (visitRequest.variant === VisitVariantType.UNSCHEDULED) {
    await AppointmentRestServiceV1.updateDescription(restOptions, existingVisit.visitId, {
      description: visitIn.description ?? '',
    });
  }

  const appointmentV1 = await AppointmentRestServiceV1.getById(restOptions, existingVisit.visitId);
  await PatientVisitMetaService.upsertVisit(patient, appointmentV1, visitRequest?.variant, apiOptions);
};

const fetchVisit = async (appointmentId: string, apiOptions?: ApiOptions): Promise<BookedVisit> =>
  BookedVisit.from(await AppointmentRestServiceV1.getById(toRestOptions(apiOptions), appointmentId));

const queryBookedVisits = async (
  bookedVisitQueryIn: BookedVisitQueryIn,
  visitRequests: VisitRequest[],
  page: Page = largePage(),
  apiOptions?: ApiOptions,
): Promise<BookedVisitsPage> => {
  const appointmentsV1 = await AppointmentRestServiceV1.query(
    toRestOptions(apiOptions),
    VisitQueryMapper.fromBookedVisitQueryIn(bookedVisitQueryIn, visitRequests),
    page,
  );
  return BookedVisitsPage.from(appointmentsV1);
};

const fetchVisits = async (
  userId: string,
  page: Page = largePage(),
  apiOptions?: ApiOptions,
): Promise<BookedVisitsPage> =>
  queryBookedVisits(
    {
      criteria: {
        participantIds: [userId],
      },
    },
    [],
    page,
    apiOptions,
  );

const fetchVisitsByIds = async (
  visitIds: string[],
  page: Page = largePage(),
  apiOptions?: ApiOptions,
): Promise<BookedVisit[]> => {
  const { appointments } = await AppointmentRestServiceV1.query(
    toRestOptions(apiOptions),
    VisitQueryMapper.fromBookedVisitQueryIn(
      {
        criteria: {
          visitIds,
        },
      },
      [],
    ),
    page,
  );

  return appointments.map(BookedVisit.from);
};

const getVisitIdsToBeExcludedFromCancel = async (
  bookedVisits: BookedVisit[],
  siteId: SiteId,
  apiOptions?: ApiOptions,
): Promise<string[]> => {
  const visitRequests = await VisitScheduleService.getCachedActiveRequestsByOrgId(siteId);
  const closeoutVisitRequests = visitRequests.filter(request => request.variant === VisitVariantType.CLOSE_OUT);

  const closeOutVisitIds = bookedVisits
    .filter(bookedVisit =>
      closeoutVisitRequests.some(visitRequest =>
        ScheduleRequestRef.isSameRequestRef(visitRequest, bookedVisit.requestRef),
      ),
    )
    .map(bookedVisit => bookedVisit.visitId);

  const visitIdsWithHbsEnabled = bookedVisits
    .filter(
      bookedVisit =>
        visitRequests.find(visitRequest => ScheduleRequestRef.isSameRequestRef(bookedVisit.requestRef, visitRequest))
          ?.hbs.enabled,
    )
    .map(bookedVisit => bookedVisit.visitId);

  const foundBioSamples: BioSample[] = visitIdsWithHbsEnabled.length
    ? (
        await BioSampleQueryService.query(
          {
            pageSize: Page.maxLimit,
            criteria: {
              visitIds: visitIdsWithHbsEnabled,
            },
          },
          undefined,
          apiOptions,
        )
      )?.data
    : [];

  const visitIdsWithBioSamples = uniq(foundBioSamples.map(sample => sample.visitId)).filter(isDefined);

  return uniq([...closeOutVisitIds, ...visitIdsWithBioSamples]);
};

const cancelVisitsInternal = async (
  visitsToBeCancelled: BookedVisit[],
  patient: Patient,
  apiOptions?: ApiOptions,
): Promise<void> => {
  // Cancel the visits in Dmdp
  const cancelPromises = visitsToBeCancelled.map(({ visitId }) =>
    AppointmentRestServiceV1.deleteById(toRestOptions(apiOptions), visitId),
  );
  await Promise.all(cancelPromises);

  // Delete the zoom meetings.
  await Promise.all(visitsToBeCancelled.map(visit => deleteVideoMeetingIfNeeded(visit, apiOptions)));

  // Clear patient's meta data from cancelled visits information
  await PatientVisitMetaService.clearVisits(
    patient,
    visitsToBeCancelled.map(({ visitId }) => visitId),
    apiOptions,
  );
};

const cancelVisit = async (visit: BookedVisit, patient: Patient, apiOptions?: ApiOptions): Promise<void> => {
  await cancelVisitsInternal([visit], patient, apiOptions);
};

const cancelAllVisits = async (
  patient: Patient,
  cancelSettings?: CancelSettings,
  apiOptions?: ApiOptions,
): Promise<void> => {
  const siteId = patient.firstOrgId;

  // Query for patient's booked visits
  const fields = [AppointmentIncludeFieldV1.LOCATION, AppointmentIncludeFieldV1.REQUEST_REF];
  const visitCriteriaIn = VisitQueryMapper.fromBookedVisitQueryIn(
    {
      fields,
      criteria: {
        onlyUpcoming: !cancelSettings?.includePassed,
        locationTypes: cancelSettings?.onlyLocationTypes,
        patient,
      },
    },
    [],
  );
  const appointmentsV1 = await AppointmentRestServiceV1.query(toRestOptions(apiOptions), visitCriteriaIn);

  // Remove visits that shall not be cancelled (optionally)
  const bookedVisits = appointmentsV1.appointments.map(BookedVisit.from);

  const excludeVisitIds =
    siteId && !cancelSettings?.evenExcluded
      ? await getVisitIdsToBeExcludedFromCancel(bookedVisits, siteId, apiOptions)
      : [];

  const visitsToBeCancelled = bookedVisits.filter(visit => !excludeVisitIds.includes(visit.visitId));

  await cancelVisitsInternal(visitsToBeCancelled, patient, apiOptions);
};

const getFirstStudyCloseoutInfo = async (study: Study): Promise<CloseoutInfo> => {
  // Closeout visit request must be configured on study level
  const visitRequests = await VisitScheduleService.getCachedActiveRequestsByOrgId(study.orgId);

  const firstCloseoutRequest = visitRequests.find(request => request.variant === VisitVariantType.CLOSE_OUT);

  StateAssert.notNull(firstCloseoutRequest, 'There is no closeout visit request configured');

  const scheduleReferenceDates: ScheduleReferenceDates = {
    orgCustomStatuses: study.customStatuses,
  };

  const getWindowInfo = async (customPacdDate?: ZonedDateTime): Promise<Maybe<WindowInfo>> => {
    const rolloutResult = await SchedulingService.rolloutRequest({
      request: firstCloseoutRequest,
      scheduleReferenceDates,
      startActionTimestamp: customPacdDate,
      fromRepeatIndex: 0,
      limitRepeat: 1,
    });
    const closeoutRequestWindow = rolloutResult.requestWindows[0] as Maybe<RequestWindow>;
    return closeoutRequestWindow
      ? WindowInfo.from(closeoutRequestWindow.windowStart, closeoutRequestWindow.windowEnd)
      : undefined;
  };

  return {
    enabled: study.statuses.closeoutTrackerStatus.status === CloseoutTrackerStatusType.ACTIVE,
    visitRequest: firstCloseoutRequest,
    getWindowInfo,
  };
};

export const VisitService = {
  addPatientVisit,
  cancelVisit,
  fetchVisit,
  fetchVisits,
  fetchVisitsByIds,
  updateVisit,
  cancelAllVisits,
  getFirstStudyCloseoutInfo,
  queryBookedVisits,
};
